import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import {
  DECISION_STRATEGY_OPTIONS,
  PERMISSION_TYPE_OPTIONS,
} from '../../../shared/components/ui/pretty-select/common-options';
import { PrettySelectComponent } from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { PermissionsService } from '../../../shared/services/permissions.service';
import {
  PermissionDTO,
  ScopeDTO,
} from '../../../shared/types/permission.types';

@Component({
  selector: 'app-permission-create',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    PageHeaderComponent,
    PrettySelectComponent,
  ],
  templateUrl: './permission-create.component.html',
})
export class PermissionCreateComponent implements OnInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly permissionsService = inject(PermissionsService);
  private readonly router = inject(Router);

  readonly typeOptions = PERMISSION_TYPE_OPTIONS;
  readonly decisionStrategyOptions = DECISION_STRATEGY_OPTIONS;

  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);

  readonly availableScopes = signal<ScopeDTO[]>([]);
  readonly selectedScopes = signal<Set<string>>(new Set());
  readonly loadingScopes = signal(false);

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(80)]],
    displayName: [''],
    type: ['scope' as 'scope' | 'resource'],
    resourceType: [''],
    decisionStrategy: ['UNANIMOUS' as 'UNANIMOUS' | 'AFFIRMATIVE' | 'CONSENSUS'],
    policiesRaw: [''], // comma- or newline-separated list of policy names
    ownerManagedAccess: [false],
  });

  ngOnInit(): void {
    this.loadingScopes.set(true);
    this.permissionsService.listScopes().subscribe({
      next: (res) => {
        this.availableScopes.set(
          (res ?? []).sort((a, b) => a.name.localeCompare(b.name)),
        );
        this.loadingScopes.set(false);
      },
      error: () => this.loadingScopes.set(false),
    });
  }

  toggleScope(name: string): void {
    const next = new Set(this.selectedScopes());
    if (next.has(name)) next.delete(name);
    else next.add(name);
    this.selectedScopes.set(next);
  }
  isScopeSelected(name: string): boolean {
    return this.selectedScopes().has(name);
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const policies = raw.policiesRaw
      .split(/[,\n]/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    const payload: PermissionDTO = {
      name: raw.name.trim(),
      displayName: raw.displayName?.trim() || undefined,
      type: raw.type,
      resourceType: raw.resourceType?.trim() || undefined,
      decisionStrategy: raw.decisionStrategy,
      ownerManagedAccess: raw.ownerManagedAccess,
      scopes: [...this.selectedScopes()],
      policies: policies.length ? policies : undefined,
    };

    this.submitting.set(true);
    this.serverError.set(null);
    this.permissionsService.create(payload).subscribe({
      next: (created) => {
        this.submitting.set(false);
        void this.router.navigate(
          created.id ? ['/permissions', created.id] : ['/permissions'],
        );
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.serverError.set(
          toErrorMessage(err, 'La création a échoué.', {
            409: 'Une permission avec ce nom existe déjà.',
          }),
        );
      },
    });
  }

  onCancel(): void { void this.router.navigate(['/permissions']); }

  showError(name: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[name];
    return c.invalid && (c.touched || c.dirty);
  }
}
